import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import { animalPartyMultipliersSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { classifyReturn } from './inheritance.js';
import { prettyGame } from '../games.js';
import {
  ANP_DEFAULT_RETURNS,
  ANP_RETURN_BPS_STEP,
  ANP_SPOTS,
  animalPartySpotRtp,
  animalPartyWinChance,
  type AnimalPartySpot,
} from '../animalparty.js';

export interface AnimalPartyMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

interface AnimalPartyMultipliersValue {
  version: number;
  returns: Record<string, number>;
}

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10000;

const KIND_NOTE: Record<AnimalPartySpot['kind'], string> = {
  animal: 'Single animals. Each wins only when the light stops on that animal.',
  group:
    'Groups. Each wins on any animal in the group. Panda, Elephant and Gorilla are in neither.',
};

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/** Human hint: total-return bps → "5× return (4:1)". */
function returnHint(bps: number): string {
  const trim = (n: number): string => String(Number(n.toFixed(2)));
  return `${trim(bps / BPS_SCALE)}× return (${trim((bps - BPS_SCALE) / BPS_SCALE)}:1)`;
}

/**
 * Animal Party payout-multiplier editor. One total-return-bps input per spot:
 * the nine single animals and the two groups.
 *
 * These multipliers are the game's ONLY tunable RTP surface. The outcome
 * weights are a versioned constant, so each spot's chance of winning is fixed
 * and its RTP is exact, shown live as you type.
 *
 * In integrator scope: rows show inherited/override tags and per-row revert,
 * and readForm() submits only the override keys. In provider scope: the full
 * table, no inheritance UI. Changes ride the two-admin approval flow.
 */
export async function renderAnimalPartyMultipliersPage(
  root: HTMLElement,
  deps: AnimalPartyMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;
  const defaults = ANP_DEFAULT_RETURNS;
  const keys = ANP_SPOTS.map((s) => s.key);
  const defaultValue: AnimalPartyMultipliersValue = { version: 1, returns: { ...defaults } };
  const stepMultiple = ANP_RETURN_BPS_STEP / BPS_SCALE;
  const gridCredits = BPS_SCALE / ANP_RETURN_BPS_STEP;

  function rowHtml(s: AnimalPartySpot, first: boolean): string {
    const key = s.key;
    const tagCell = isIntegratorScope
      ? `<td data-tag="${key}" class="muted" style="min-width:80px">—</td>`
      : `<td class="muted">default ${defaults[key]} bps</td>`;
    const revertCell = isIntegratorScope
      ? `<td><button data-revert="${key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
      : `<td></td>`;
    const heading = first
      ? `<tr><td colspan="7" class="muted" style="padding-top:14px;font-size:12px">${KIND_NOTE[s.kind]}</td></tr>`
      : '';
    return `${heading}<tr data-row="${key}" data-source="inherited">
      <td><strong>${s.label}</strong><div class="muted" style="font-size:12px"><code>${key}</code></div></td>
      <td><input data-bet="${key}" type="number" min="${ANP_RETURN_BPS_STEP}" max="100000000" step="${ANP_RETURN_BPS_STEP}" style="width:110px" /></td>
      <td class="muted" data-hint="${key}">—</td>
      <td class="muted" style="white-space:nowrap">${(animalPartyWinChance(key) * 100).toFixed(4)}%</td>
      <td data-rtp="${key}" style="white-space:nowrap">—</td>
      ${tagCell}
      ${revertCell}
    </tr>`;
  }

  const resetAllBtn = isIntegratorScope
    ? `<button id="apm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>50000 = 5× return</code>. Every rate must be a multiple of ${ANP_RETURN_BPS_STEP} (${stepMultiple}×) — anything else takes wins off the ${gridCredits}-credit stake grid, and the save is refused. The King pays no spot, so no row here prices it. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Per-spot return (bps)</h2>
      <table>
        <thead><tr><th>Spot</th><th>Return (bps)</th><th>Multiple</th><th>P(win)</th><th>RTP</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="apm-rows">${ANP_SPOTS.map((s, i) => rowHtml(s, i === 0 || ANP_SPOTS[i - 1]!.kind !== s.kind)).join('')}</tbody>
      </table>
      <p class="muted" id="apm-headline" style="margin-top:10px">—</p>
      <div class="row" style="margin-top:14px">
        <button id="apm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="apm-save" class="primary">Propose change</button>
        <span id="apm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="apm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#apm-rows')!;
  const headlineEl = root.querySelector<HTMLElement>('#apm-headline')!;
  const msgEl = root.querySelector<HTMLElement>('#apm-msg')!;

  // Per-row inherited values for revert (integrator scope only).
  const inheritedValues = new Map<string, number>();

  const inputs = new Map<string, HTMLInputElement>();
  for (const key of keys) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) inputs.set(key, el);
  }

  function refreshDerived(): void {
    let bestKey = keys[0]!;
    let best = -Infinity;
    for (const key of keys) {
      const bps = Number(inputs.get(key)?.value) || 0;
      const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
      if (hintEl) {
        const offStep =
          bps <= 0 || bps % ANP_RETURN_BPS_STEP !== 0
            ? ` <span style="color:var(--error)">not a positive multiple of ${ANP_RETURN_BPS_STEP} — will be refused</span>`
            : '';
        hintEl.innerHTML = `${returnHint(bps)}${offStep}`;
      }
      const rtp = animalPartySpotRtp(key, bps);
      const rtpEl = rowsEl.querySelector<HTMLElement>(`[data-rtp="${key}"]`);
      if (rtpEl) {
        const over = rtp > 1 ? ' <span style="color:var(--error)">over 100%!</span>' : '';
        rtpEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(2)}%</strong>${over}`;
      }
      if (rtp > best) {
        best = rtp;
        bestKey = key;
      }
    }
    // Headline is the BEST spot on the board, matching the RTP hero above
    // (admin-api takes the max across bet types, not an average).
    headlineEl.innerHTML =
      `Headline RTP (best spot on the board): <strong>${ANP_SPOTS.find((s) => s.key === bestKey)!.label}</strong> at ` +
      `<strong style="color:${rtpColor(best)}">${(best * 100).toFixed(2)}%</strong> — house edge ` +
      `<strong>${((1 - best) * 100).toFixed(2)}%</strong>.`;
  }

  function markRow(key: string, source: 'override' | 'inherited'): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    const isOverride = source === 'override';
    if (row) row.dataset.source = source;
    if (tagEl) {
      tagEl.textContent = source;
      tagEl.style.color = isOverride ? 'var(--accent)' : 'var(--text-dim)';
      tagEl.style.fontWeight = isOverride ? '600' : '';
    }
    if (revertEl) revertEl.style.display = isOverride ? '' : 'none';
  }

  /** Seed rows from a full value (provider scope or reset). */
  function fillForm(value: AnimalPartyMultipliersValue): void {
    for (const [key, input] of inputs) input.value = String(value.returns[key] ?? defaults[key]);
    refreshDerived();
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, defaults[key]!);
      input.value = String(classified.value);
      inheritedValues.set(
        key,
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : defaults[key]!,
      );
      markRow(key, classified.source);
    }
    refreshDerived();
  }

  /** Build the propose payload. In integrator scope, only the override keys. */
  function readForm(): AnimalPartyMultipliersValue {
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) {
      const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
      if (deps.isProviderScope || row?.dataset.source === 'override') {
        returns[key] = Math.round(Number(input.value) || 0);
      }
    }
    return { version: 1, returns };
  }

  for (const [key, input] of inputs) {
    input.addEventListener('input', () => {
      if (isIntegratorScope) markRow(key, 'override');
      refreshDerived();
    });
  }

  if (isIntegratorScope) {
    for (const key of keys) {
      rowsEl
        .querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`)
        ?.addEventListener('click', () => {
          const input = inputs.get(key);
          if (input) input.value = String(inheritedValues.get(key) ?? defaults[key]);
          markRow(key, 'inherited');
          refreshDerived();
        });
    }

    root.querySelector<HTMLButtonElement>('#apm-reset-all')?.addEventListener('click', () => {
      for (const [key, input] of inputs) {
        input.value = String(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
      }
      refreshDerived();
      msgEl.textContent = 'All spots reset to provider/baked defaults (not saved).';
      msgEl.className = 'muted';
    });
  }

  /** Re-read the surface after a save/approve, respecting the current scope. */
  async function reload(): Promise<void> {
    if (isIntegratorScope) {
      const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
      const own = inh.ownOverride.multipliers as Partial<AnimalPartyMultipliersValue> | undefined;
      const prov = inh.providerDefault.multipliers as
        | Partial<AnimalPartyMultipliersValue>
        | undefined;
      fillFormInherited(own?.returns, prov?.returns);
      return;
    }
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = value as Partial<AnimalPartyMultipliersValue> | null;
    fillForm(stored?.returns ? { version: 1, returns: stored.returns } : defaultValue);
  }

  await reload();

  root.querySelector<HTMLButtonElement>('#apm-reset')!.addEventListener('click', () => {
    fillForm(defaultValue);
    if (isIntegratorScope) for (const key of keys) markRow(key, 'override');
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#apm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = animalPartyMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent =
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#apm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
  }

  await refreshPending();
}
